import React, { createContext, useContext, ReactNode } from 'react';
import { UserRole } from '../data/types';

interface PermissionContextType {
  canCreate: (resource: string) => boolean;
  canEdit: (resource: string) => boolean;
  canDelete: (resource: string) => boolean;
  canView: (resource: string) => boolean;
  canManageDistrict: boolean;
  canManageAllSchools: boolean;
  canUploadGames: boolean;
  canModifyQuestions: boolean;
  canAccessCsvImport: boolean;
  canAccessConcurrentFeature: boolean;
  canConfigurePlatform: boolean;
  canViewDistrictWideReports: boolean;
  getAssignedSchoolIds: () => string[];
  userRole: UserRole;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
  userRole: UserRole;
  assignedSchoolIds?: string[];
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ 
  children, 
  userRole,
  assignedSchoolIds = []
}) => {
  // Define permissions based on user role
  const canCreate = (resource: string): boolean => {
    if (userRole === 'SuperAdmin') return true;
    
    if (userRole === 'SchoolAdmin') {
      // School admins can create certain resources but not others
      const allowedResources = ['learning-paths', 'reports', 'student-groups'];
      return allowedResources.includes(resource);
    }
    
    if (userRole === 'Teacher') {
      const allowedResources = ['learning-paths', 'student-groups'];
      return allowedResources.includes(resource);
    }
    
    return false;
  };
  
  const canEdit = (resource: string): boolean => {
    if (userRole === 'SuperAdmin') return true;
    
    if (userRole === 'SchoolAdmin') {
      // School admins can edit certain resources but not others
      const allowedResources = [
        'learning-paths', 
        'school-settings', 
        'skip-and-move-policy',
        'learning-phase-progression'
      ];
      return allowedResources.includes(resource);
    }
    
    if (userRole === 'Teacher') {
      const allowedResources = ['learning-paths', 'student-groups', 'class-settings'];
      return allowedResources.includes(resource);
    }
    
    return false;
  };
  
  const canDelete = (resource: string): boolean => {
    if (userRole === 'SuperAdmin') return true;
    
    if (userRole === 'SchoolAdmin') {
      // School admins can delete very few resources
      const allowedResources = ['learning-paths'];
      return allowedResources.includes(resource);
    }
    
    if (userRole === 'Teacher') {
      const allowedResources = ['learning-paths', 'student-groups'];
      return allowedResources.includes(resource);
    }
    
    return false;
  };
  
  const canView = (resource: string): boolean => {
    if (userRole === 'SuperAdmin') return true;
    
    if (userRole === 'SchoolAdmin') {
      // School admins can view most resources but not all
      const restrictedResources = ['district-management', 'csv-import-management'];
      return !restrictedResources.includes(resource);
    }
    
    if (userRole === 'Teacher') {
      const allowedResources = [
        'classes', 
        'students', 
        'learning-paths', 
        'standards', 
        'games',
        'reports',
        'student-groups'
      ];
      return allowedResources.includes(resource);
    }
    
    return false;
  };
  
  // Role-specific permissions
  const canManageDistrict = userRole === 'SuperAdmin';
  const canManageAllSchools = userRole === 'SuperAdmin';
  const canUploadGames = userRole === 'SuperAdmin';
  const canModifyQuestions = userRole === 'SuperAdmin';
  const canAccessCsvImport = userRole === 'SuperAdmin';
  const canAccessConcurrentFeature = userRole === 'SuperAdmin';
  const canConfigurePlatform = userRole === 'SuperAdmin';
  const canViewDistrictWideReports = userRole === 'SuperAdmin';
  
  // Function to get assigned school IDs
  const getAssignedSchoolIds = (): string[] => {
    if (userRole === 'SuperAdmin') {
      // Super admins have access to all schools, but we don't have that list here
      // In a real app, this would fetch all school IDs
      return ['all'];
    }
    
    if (userRole === 'SchoolAdmin') {
      // Return the assigned school IDs for school admin
      return assignedSchoolIds;
    }
    
    // For teachers, this would return their school ID
    return [];
  };
  
  const value = {
    canCreate,
    canEdit,
    canDelete,
    canView,
    canManageDistrict,
    canManageAllSchools,
    canUploadGames,
    canModifyQuestions,
    canAccessCsvImport,
    canAccessConcurrentFeature,
    canConfigurePlatform,
    canViewDistrictWideReports,
    getAssignedSchoolIds,
    userRole
  };
  
  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
